/*
 * Episode metadata + bulk loaders. Phase 1.
 * Hits /api/episode/{uuid} and /api/episode/{uuid}/v2/scenes.
 * In local mode, delegates to localBackend (Phase 7).
 */

import type {
  Character,
  Episode,
  Scene,
  SceneImage,
  SceneVideo,
} from '@/types';
import { isLocalMode } from '@/local';
import { apiFetch } from './apiBase';
import { getLocalEpisodeBundle } from '@/local/localBackend';

export interface EpisodeBundle {
  episode: Episode;
  characters: Character[];
  scenes: Scene[];
  images: SceneImage[];
  videos: SceneVideo[];
}

export async function loadEpisodeBundle(uuid: string): Promise<EpisodeBundle> {
  if (isLocalMode()) return getLocalEpisodeBundle(uuid);
  const base = `/api/episode/${encodeURIComponent(uuid)}/v2`;
  const [episode, characters, scenes, images, videos] = await Promise.all([
    fetchJson<Episode>(`${base}`),
    fetchJson<Character[]>(`${base}/characters`),
    fetchJson<Scene[]>(`${base}/scenes`),
    fetchJson<SceneImage[]>(`${base}/images`),
    fetchJson<SceneVideo[]>(`${base}/videos`),
  ]);
  return { episode, characters, scenes, images, videos };
}

async function fetchJson<T>(url: string): Promise<T> {
  const res = await apiFetch(url);
  if (res.status === 403) {
    throw new EpisodeAccessError();
  }
  if (!res.ok) {
    throw new Error(`${url} failed: ${res.status}`);
  }
  return res.json();
}

export class EpisodeAccessError extends Error {
  constructor(message = 'No access to this episode.') {
    super(message);
    this.name = 'EpisodeAccessError';
  }
}
